import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { canManageInfra, isInfraAdmin } from "../rbac";

/** The slice of a raw Feathers client service this store calls. */
interface RawService {
  find: (params?: object) => Promise<unknown>;
  create: (data: object, params?: object) => Promise<unknown>;
  patch: (id: string, data: object, params?: object) => Promise<unknown>;
}
import {
  connectionReadiness,
  inquiryKeyProblem,
  type InstanceConnection,
  type InstanceConnectionFormData,
  type InstanceEnvironment,
  type InstanceKeyConfig,
  type InstanceRemoteAuditRow,
  type InstanceUser,
  type InstanceUserFormData,
  type InstanceUserWriteResult,
  type OneTimeSecret,
} from "./instanceUsers.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * What a dialog-driven write returns when the page moved to another client (or
 * left Client 360) before the reply came: neither success nor a failure to show
 * — the dialog helper must NOT reopen the form, which would put this client's
 * form in front of another client.
 */
export const ABANDONED = "abandoned" as const;
export type SubmitResult = boolean | typeof ABANDONED;

/** Rows per page of the instance's user list — the API caps a page at 200. */
export const INSTANCE_USERS_PAGE_SIZE = 50;
/** How much recent activity the tab shows. */
export const INSTANCE_AUDIT_LIMIT = 25;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FindResponse<T> = T[] | { data?: T[]; total?: number } | null | undefined;

function rowsOf<T>(result: FindResponse<T>): T[] {
  if (Array.isArray(result)) return result;
  return Array.isArray(result?.data) ? result.data : [];
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * The Feathers transport gives up on a call with a `Timeout` (408) while HQ's
 * API may still be finishing it against the instance. For a write that is NOT a
 * refusal — the change may well have landed — so say so instead of "failed".
 */
const isStoreTimeout = (err: unknown): boolean => (err as { code?: unknown })?.code === 408;

const emptyConnectionForm = (): InstanceConnectionFormData => ({
  environment: "prod",
  apiBaseUrl: "https://",
  webBaseUrl: "",
  appName: "",
  serviceUserEmail: "",
});

const emptyUserForm = (): InstanceUserFormData => ({
  firstName: "",
  lastName: "",
  email: "",
  department: "",
  team: "",
  copyAccessFromUserId: "",
});

/**
 * Users on a CLIENT INSTANCE, managed from Client 360's Users tab.
 *
 * Every call goes to HQ's own API (`instance-connections`, `instance-users`,
 * `instance-remote-audit`), which reaches the client instance server-to-server;
 * the browser never talks to a client instance. They are called through the RAW
 * Feathers client, not ServiceStores (see `service()`): a ServiceStore caches
 * create responses, and these carry one-time secrets. The raw client rejects on
 * every refusal, so each lands in a try/catch here and is shown.
 *
 * The rows are the find RESULT for the selected connection (the same shape as
 * ClientDetailStore's instances), because one client's users on one instance
 * are not HQ entities and must not mix with another instance's in a shared cache.
 * Every load is guarded (`clientEpoch`, `usersRequest`, …): a reply that arrives
 * after the client, environment or search changed is dropped.
 *
 * Temporary passwords and a new key's config entry live in `secret` / `issuedKey`
 * only until the dialog showing them closes (`clearSecret` / `clearIssuedKey`),
 * and nowhere else in the page.
 *
 * RBAC mirrors the API: viewing is staff, user writes are operator+, connection
 * settings are admin. The API re-checks every call.
 */
class InstanceUsersStore {
  rootStore: RootStore;

  clientId: string | null = null;

  // ---- Connections (one per environment) ----
  connections: InstanceConnection[] = [];
  connectionsLoading = false;
  connectionsError: string | null = null;
  selectedConnectionId: string | null = null;

  // ---- Users on the selected instance ----
  users: InstanceUser[] = [];
  usersTotal = 0;
  usersLoading = false;
  usersError: string | null = null;
  search = "";
  showDisabled = true;

  // ---- Add / edit form ----
  editingUserId: string | null = null;
  form: InstanceUserFormData = emptyUserForm();
  saving = false;
  formError: string | null = null;
  /** Candidates for "same access as" — a small search over the same instance. */
  copyCandidates: InstanceUser[] = [];

  // ---- Row actions ----
  busyUserId: string | null = null;
  actionError: string | null = null;
  /** The action awaiting confirmation (disable / reset-password). */
  pendingConfirm: { user: InstanceUser; action: "disable" | "reset-password" } | null = null;

  /** Shown once, then cleared. Never persisted anywhere. */
  secret: OneTimeSecret | null = null;

  // ---- Recent activity ----
  audit: InstanceRemoteAuditRow[] = [];
  auditLoading = false;

  // ---- Connection settings (admin) ----
  editingConnectionId: string | null = null;
  connectionForm: InstanceConnectionFormData = emptyConnectionForm();
  connectionSaving = false;
  /** Why the connection dialog's Save was refused — its route throws this,
   *  so it shows in the dialog's footer. */
  connectionError: string | null = null;
  /** Why Issue key / Check connection (buttons inside the dialog body, not its
   *  submit) failed — shown in the body. */
  keyActionError: string | null = null;
  checkingConnection = false;
  issuingKey = false;
  /** The config entry for the key just issued. Shown once, then cleared. */
  issuedKey: InstanceKeyConfig | null = null;

  // ---- The inquiry publishing key (admin; Decision 5, 2026-11-02) ----
  /**
   * What the administrator is typing: the instance's clientInquiries.inboundSecret.
   * WRITE-ONLY — HQ never sends it back, so this is the only copy the page ever
   * holds; it is cleared once saved and whenever the dialog opens or closes.
   */
  inquiryKeyDraft = "";
  savingInquiryKey = false;
  inquiryKeyError: string | null = null;
  /** What the last save or removal did, said once. */
  inquiryKeyNotice: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /**
   * The RAW Feathers service — deliberately not a ServiceStore. A ServiceStore
   * writes every create response into its entity cache, and here the responses
   * carry a new user's temporary password (`{ user, temporaryPassword }`) and a
   * new key's config entry: they must live exactly as long as the dialog that
   * shows them, and a cache keeps them for the session. The raw client also
   * rejects on every refusal, so none can read as success.
   */
  private service(path: string): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service(path) ?? null;
  }

  private get connectionsService() {
    return this.service("instance-connections");
  }

  private get usersService() {
    return this.service("instance-users");
  }

  private get auditService() {
    return this.service("instance-remote-audit");
  }

  /**
   * Bumped whenever the client changes. A reply for the previous client is
   * dropped on arrival — otherwise a slow answer for client A could install A's
   * connection while the page shows client B, and the next "Add user" would
   * land on A's instance.
   */
  private clientEpoch = 0;
  /** Bumped per connections request: an older list must not replace a newer one. */
  private connectionsRequest = 0;
  /** Bumped per user-list request; only the latest one may write the list. */
  private usersRequest = 0;
  private auditRequest = 0;
  private candidatesRequest = 0;

  // ---- Derived ----

  get canManageUsers(): boolean {
    return canManageInfra(this.rootStore);
  }

  get canManageConnections(): boolean {
    return isInfraAdmin(this.rootStore);
  }

  get selectedConnection(): InstanceConnection | null {
    return this.connections.find((c) => c.id === this.selectedConnectionId) ?? null;
  }

  get readiness() {
    return connectionReadiness(this.selectedConnection);
  }

  get isEditingUser(): boolean {
    return !!this.editingUserId;
  }

  get isUserFormValid(): boolean {
    const f = this.form;
    return !!f.firstName.trim() && !!f.lastName.trim() && EMAIL_RE.test(f.email.trim());
  }

  get isConnectionFormValid(): boolean {
    const f = this.connectionForm;
    return /^https?:\/\/\S+/.test(f.apiBaseUrl.trim()) && f.apiBaseUrl.trim() !== "https://" && EMAIL_RE.test(f.serviceUserEmail.trim());
  }

  get hasMoreUsers(): boolean {
    return this.users.length < this.usersTotal;
  }

  get visibleUsers(): InstanceUser[] {
    return this.showDisabled ? this.users : this.users.filter((u) => u.isValidated);
  }

  userById(id: string | null | undefined): InstanceUser | null {
    if (!id) return null;
    return this.users.find((u) => u.id === id) ?? this.copyCandidates.find((u) => u.id === id) ?? null;
  }

  // ---- Lifecycle ----

  /** Enter the tab for a client. Loads its connections, then the first usable one. */
  async initialize(clientId: string) {
    if (this.clientId !== clientId) {
      this.reset();
      this.clientId = clientId;
    }
    await this.loadConnections();
  }

  reset() {
    this.clientEpoch++;
    this.usersRequest++;
    this.auditRequest++;
    this.candidatesRequest++;
    this.clientId = null;
    this.connections = [];
    this.selectedConnectionId = null;
    this.users = [];
    this.usersTotal = 0;
    this.usersError = null;
    this.connectionsError = null;
    this.audit = [];
    this.search = "";
    this.secret = null;
    this.issuedKey = null;
    this.actionError = null;
    // In-flight flags belong to the client being left; their replies are
    // dropped (see the epoch guards), so nothing else would ever clear them.
    this.connectionsLoading = false;
    this.usersLoading = false;
    this.auditLoading = false;
    this.saving = false;
    this.busyUserId = null;
    this.pendingConfirm = null;
    this.connectionSaving = false;
    this.issuingKey = false;
    this.checkingConnection = false;
    this.copyCandidates = [];
    // Forms too: a dialog must never reopen on another client with this one's
    // half-filled form or target id.
    this.editingUserId = null;
    this.form = emptyUserForm();
    this.formError = null;
    this.editingConnectionId = null;
    this.connectionForm = emptyConnectionForm();
    this.connectionError = null;
    this.keyActionError = null;
    // An inquiry publishing key typed and not saved is one client's secret: it
    // never survives a sign-out, nor a switch to another client.
    this.inquiryKeyDraft = "";
    this.savingInquiryKey = false;
    this.inquiryKeyError = null;
    this.inquiryKeyNotice = null;
  }

  async loadConnections() {
    if (!this.clientId) return;
    const epoch = this.clientEpoch;
    const request = ++this.connectionsRequest;
    const stale = () => epoch !== this.clientEpoch || request !== this.connectionsRequest;
    this.connectionsLoading = true;
    this.connectionsError = null;
    try {
      const result = await this.connectionsService?.find({ query: { clientId: this.clientId } });
      // Another client, or a newer load of this one (e.g. the refresh after a
      // connection was added): an older list must not remove what it lacks.
      if (stale()) return;
      const rows = rowsOf<InstanceConnection>(result as unknown as FindResponse<InstanceConnection>);
      runInAction(() => {
        this.connections = rows;
        if (!rows.some((c) => c.id === this.selectedConnectionId)) {
          // Default to production, else whatever exists.
          this.selectedConnectionId = (rows.find((c) => c.environment === "prod") ?? rows[0])?.id ?? null;
        }
      });
      if (this.selectedConnection?.hasApiKey) {
        await Promise.all([this.loadUsers(), this.loadAudit()]);
      } else {
        this.usersRequest++;
        runInAction(() => {
          this.users = [];
          this.usersTotal = 0;
        });
        await this.loadAudit();
      }
    } catch (err) {
      if (stale()) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.connectionsError = messageOf(err, "Could not load this client's instance connections");
      });
    } finally {
      if (!stale()) {
        runInAction(() => {
          this.connectionsLoading = false;
        });
      }
    }
  }

  async selectConnection(id: string) {
    if (id === this.selectedConnectionId) return;
    this.usersRequest++;
    this.auditRequest++;
    this.candidatesRequest++;
    this.copyCandidates = [];
    this.selectedConnectionId = id;
    this.users = [];
    this.usersTotal = 0;
    this.usersError = null;
    this.actionError = null;
    if (this.selectedConnection?.hasApiKey) await Promise.all([this.loadUsers(), this.loadAudit()]);
    else await this.loadAudit();
  }

  // ---- Users ----

  setSearch(value: string) {
    this.search = value;
  }

  setShowDisabled(value: boolean) {
    this.showDisabled = value;
  }

  async loadUsers(skip = 0) {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return;
    // Only the LATEST request may write the list: a slow answer for an older
    // search, page, environment or client is dropped on arrival.
    const request = ++this.usersRequest;
    this.usersLoading = true;
    this.usersError = null;
    try {
      const result = (await this.usersService?.find({
        query: {
          connectionId,
          $limit: INSTANCE_USERS_PAGE_SIZE,
          $skip: skip,
          ...(this.search.trim() ? { $search: this.search.trim() } : {}),
        },
      })) as unknown as FindResponse<InstanceUser>;
      if (request !== this.usersRequest) return;
      runInAction(() => {
        const rows = rowsOf(result);
        this.users = skip === 0 ? rows : [...this.users, ...rows];
        this.usersTotal =
          result && !Array.isArray(result) && typeof result.total === "number" ? result.total : this.users.length;
      });
    } catch (err) {
      if (request !== this.usersRequest) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.usersError = messageOf(err, "Could not load users from the instance");
      });
    } finally {
      if (request === this.usersRequest) {
        runInAction(() => {
          this.usersLoading = false;
        });
      }
    }
  }

  async loadMoreUsers() {
    await this.loadUsers(this.users.length);
  }

  async searchCopyCandidates(text: string) {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return;
    const request = ++this.candidatesRequest;
    try {
      const result = (await this.usersService?.find({
        query: { connectionId, $limit: 20, ...(text.trim() ? { $search: text.trim() } : {}) },
      })) as unknown as FindResponse<InstanceUser>;
      if (request !== this.candidatesRequest) return;
      runInAction(() => {
        // Administrators are not offered: HQ refuses to copy an admin's access.
        this.copyCandidates = rowsOf(result).filter((u) => u.id !== this.editingUserId && !u.adminUser);
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (request !== this.candidatesRequest) return;
      // A failed suggestion list is not worth an error banner; the picker stays empty.
      runInAction(() => {
        this.copyCandidates = [];
      });
    }
  }

  // ---- Add / edit ----

  startAdd() {
    this.editingUserId = null;
    this.form = emptyUserForm();
    this.formError = null;
    this.copyCandidates = [];
    void this.searchCopyCandidates("");
  }

  startEdit(user: InstanceUser) {
    this.editingUserId = user.id;
    this.form = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      department: user.department ?? "",
      team: user.team ?? "",
      copyAccessFromUserId: "",
    };
    this.formError = null;
  }

  setFormField(field: keyof InstanceUserFormData, value: string) {
    this.form = { ...this.form, [field]: value };
  }

  /** Save the add/edit form. Returns true when the dialog may close. */
  async saveUser(): Promise<SubmitResult> {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return false;
    if (!this.isUserFormValid) {
      this.formError = "First name, last name and a valid email are required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    // A reply that lands after the page moved to another client writes nothing
    // here: its dialog is gone, and the audit trail already holds the change.
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    const f = this.form;
    const profile: Record<string, unknown> = {
      firstName: f.firstName.trim(),
      lastName: f.lastName.trim(),
      email: f.email.trim(),
      department: f.department.trim() || null,
      team: f.team.trim() || null,
    };
    try {
      if (this.editingUserId) {
        await this.usersService?.patch(this.editingUserId, profile as never, { query: { connectionId } } as never);
      } else {
        const result = (await this.usersService?.create({
          connectionId,
          ...profile,
          ...(f.copyAccessFromUserId ? { copyAccessFromUserId: f.copyAccessFromUserId } : {}),
        } as never)) as unknown as InstanceUserWriteResult;
        if (left()) return ABANDONED;
        if (!result?.temporaryPassword) throw new Error("The instance did not confirm the new user");
        runInAction(() => {
          this.secret = {
            kind: "new-user",
            email: result.user.email,
            password: result.temporaryPassword as string,
            accessCopy: result.accessCopy,
          };
        });
      }
      if (left()) return ABANDONED;
      runInAction(() => {
        this.editingUserId = null;
        this.form = emptyUserForm();
      });
      await Promise.all([this.loadUsers(), this.loadAudit()]);
      if (left()) return ABANDONED; // the page moved on during the refresh
      return true;
    } catch (err) {
      if (left() || isSessionEnded(err)) return ABANDONED;
      runInAction(() => {
        this.formError = isStoreTimeout(err)
          ? this.editingUserId
            ? "The instance is slow to answer. The change may still have been saved; check the list before trying again."
            : "The instance is slow to answer. The user may still have been created, and the temporary password cannot be shown now. " +
              "Check the list: if they are there, use Reset password to give them one."
          : messageOf(err, "The instance did not accept the change");
      });
      if (isStoreTimeout(err)) void this.loadUsers();
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.saving = false;
        });
      }
    }
  }

  // ---- Row actions ----

  askConfirm(user: InstanceUser, action: "disable" | "reset-password") {
    this.pendingConfirm = { user, action };
  }

  cancelConfirm() {
    this.pendingConfirm = null;
  }

  async runConfirmed(): Promise<boolean> {
    const pending = this.pendingConfirm;
    if (!pending) return false;
    const ended = sessionMark(this.rootStore);
    const result = await this.runAction(pending.user, pending.action);
    // runAction swallows a sign-out; the confirm the reset left is not this call's.
    if (ended()) return false;
    runInAction(() => {
      this.pendingConfirm = null;
    });
    // "abandoned" is not success: nothing may be revealed on a page that moved on.
    return result === true;
  }

  async enable(user: InstanceUser) {
    return this.runAction(user, "enable");
  }

  private async runAction(user: InstanceUser, action: "disable" | "enable" | "reset-password"): Promise<SubmitResult> {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return false;
    this.busyUserId = user.id;
    this.actionError = null;
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    try {
      const result = (await this.usersService?.patch(user.id, { action } as never, {
        query: { connectionId },
      } as never)) as unknown as InstanceUserWriteResult;
      // The page moved to another client: nothing here may change for it.
      if (left()) return ABANDONED;
      if (!result?.user) throw new Error("The instance did not confirm the change");
      const password = result.temporaryPassword;
      if (action === "reset-password" && !password) throw new Error("The instance did not return a new password");
      runInAction(() => {
        // The list belongs to whichever environment is selected NOW; a reply for
        // the one the person left only updates it if they are still on it. The
        // password is shown regardless — it is real, and there is no second copy.
        if (connectionId === this.selectedConnectionId) {
          this.users = this.users.map((u) => (u.id === result.user.id ? result.user : u));
        }
        if (action === "reset-password" && password) {
          this.secret = { kind: "reset-password", email: result.user.email, password };
        }
      });
      await this.loadAudit();
      return true;
    } catch (err) {
      if (left()) return ABANDONED;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError = isStoreTimeout(err)
            ? action === "reset-password"
              ? `The instance is slow to answer. ${user.email}'s password may have been changed; run Reset password again to get one you can hand over.`
              : "The instance is slow to answer. The change may still have been saved; refresh before trying again."
            : messageOf(err, "The instance did not accept the change");
        }
      });
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.busyUserId = null;
        });
      }
    }
  }

  clearActionError() {
    this.actionError = null;
  }

  clearSecret() {
    this.secret = null;
  }

  // ---- Recent activity ----

  async loadAudit() {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) {
      this.audit = [];
      return;
    }
    const request = ++this.auditRequest;
    this.auditLoading = true;
    try {
      const result = await this.auditService?.find({ query: { connectionId, $limit: INSTANCE_AUDIT_LIMIT } });
      if (request !== this.auditRequest) return;
      runInAction(() => {
        this.audit = rowsOf<InstanceRemoteAuditRow>(result as unknown as FindResponse<InstanceRemoteAuditRow>);
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (request !== this.auditRequest) return;
      // Activity is secondary; its absence is shown as an empty list.
      runInAction(() => {
        this.audit = [];
      });
    } finally {
      if (request === this.auditRequest) {
        runInAction(() => {
          this.auditLoading = false;
        });
      }
    }
  }

  // ---- Connection settings (admin) ----

  startConnectionEdit(connection: InstanceConnection | null) {
    this.connectionError = null;
    this.keyActionError = null;
    this.issuedKey = null;
    this.clearInquiryKeyDraft();
    if (connection) {
      this.editingConnectionId = connection.id;
      this.connectionForm = {
        environment: connection.environment,
        apiBaseUrl: connection.apiBaseUrl,
        webBaseUrl: connection.webBaseUrl ?? "",
        appName: connection.appName ?? "",
        serviceUserEmail: connection.serviceUserEmail,
      };
    } else {
      const used = new Set(this.connections.map((c) => c.environment));
      const environment = (["prod", "impl", "dev"] as InstanceEnvironment[]).find((e) => !used.has(e)) ?? "prod";
      this.editingConnectionId = null;
      this.connectionForm = { ...emptyConnectionForm(), environment };
    }
  }

  setConnectionField(field: keyof InstanceConnectionFormData, value: string) {
    this.connectionForm = { ...this.connectionForm, [field]: value } as InstanceConnectionFormData;
  }

  get editingConnection(): InstanceConnection | null {
    return this.connections.find((c) => c.id === this.editingConnectionId) ?? null;
  }

  async saveConnection(): Promise<SubmitResult> {
    if (!this.clientId) return false;
    if (!this.isConnectionFormValid) {
      this.connectionError = "An https API address and the service user's email are required.";
      return false;
    }
    this.connectionSaving = true;
    this.connectionError = null;
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    const f = this.connectionForm;
    const body = {
      apiBaseUrl: f.apiBaseUrl.trim(),
      // Blank clears it; the API keeps only an https origin (#17).
      webBaseUrl: f.webBaseUrl.trim() || null,
      appName: f.appName.trim() || null,
      serviceUserEmail: f.serviceUserEmail.trim(),
    };
    try {
      let saved: InstanceConnection;
      if (this.editingConnectionId) {
        saved = (await this.connectionsService?.patch(this.editingConnectionId, body as never)) as unknown as InstanceConnection;
      } else {
        saved = (await this.connectionsService?.create({
          clientId: this.clientId,
          environment: f.environment,
          ...body,
        } as never)) as unknown as InstanceConnection;
      }
      if (left()) return ABANDONED;
      if (!saved?.id) throw new Error("HQ did not confirm the connection");
      runInAction(() => {
        this.editingConnectionId = saved.id;
        this.selectedConnectionId = saved.id;
      });
      await this.loadConnections();
      // Checked AGAIN after the refresh: the page may have moved on during it,
      // and a `true` here reopens the connection dialog.
      if (left()) return ABANDONED;
      return true;
    } catch (err) {
      if (left()) return ABANDONED;
      runInAction(() => {
        if (!isSessionEnded(err)) this.connectionError = messageOf(err, "Could not save the connection");
      });
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.connectionSaving = false;
        });
      }
    }
  }

  /**
   * Issue a new key for the connection being edited. Replaces the key THIS
   * screen shows (`expectedKeyId`): if someone else issued one meanwhile, HQ
   * refuses rather than handing back an entry that would never authenticate.
   */
  async issueKey(): Promise<boolean> {
    const id = this.editingConnectionId;
    if (!id) return false;
    this.issuingKey = true;
    this.keyActionError = null;
    this.issuedKey = null;
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    const expectedKeyId = this.editingConnection?.keyId ?? null;
    try {
      const result = (await this.connectionsService?.create({
        action: "generate-key",
        id,
        expectedKeyId,
      } as never)) as unknown as InstanceConnection;
      // The page left this client: the entry has nowhere to be shown. The key
      // itself is issued (and audited); issuing again replaces it.
      if (left() || this.editingConnectionId !== id) return false;
      if (!result?.keyConfig) throw new Error("HQ did not return the key's configuration");
      runInAction(() => {
        this.issuedKey = result.keyConfig ?? null;
      });
      await this.loadConnections();
      return true;
    } catch (err) {
      if (left()) return false;
      runInAction(() => {
        if (!isSessionEnded(err)) this.keyActionError = messageOf(err, "Could not issue a key");
      });
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.issuingKey = false;
        });
      }
    }
  }

  clearIssuedKey() {
    this.issuedKey = null;
  }

  setInquiryKeyDraft(value: string) {
    this.inquiryKeyDraft = value;
    this.inquiryKeyError = null;
    this.inquiryKeyNotice = null;
  }

  clearInquiryKeyDraft() {
    this.inquiryKeyDraft = "";
    this.inquiryKeyError = null;
    this.inquiryKeyNotice = null;
  }

  /** Why the typed key cannot be saved, before HQ is asked; null when it may be. */
  get inquiryKeyDraftProblem(): string | null {
    return inquiryKeyProblem(this.inquiryKeyDraft);
  }

  /**
   * Enter the instance's inquiry publishing key on the connection being edited.
   * The answer says only THAT a key is held; the draft is dropped once HQ has it.
   */
  async saveInquiryKey(): Promise<boolean> {
    const id = this.editingConnectionId;
    const inquiryKey = this.inquiryKeyDraft.trim();
    if (!id || !inquiryKey || this.inquiryKeyDraftProblem || this.savingInquiryKey) return false;
    // The address on screen: if someone moved the connection meanwhile, HQ refuses
    // rather than keep this key beside an address it was not entered for.
    const expectedApiBaseUrl = this.editingConnection?.apiBaseUrl;
    return this.inquiryKeyAction(
      id,
      { action: "set-inquiry-key", inquiryKey, ...(expectedApiBaseUrl ? { expectedApiBaseUrl } : {}) },
      "Saved. HQ keeps it encrypted and will not show it again; it puts released questions into this app with it.",
      "Could not save the inquiry publishing key",
    );
  }

  /** HQ forgets the key: nothing is put into this client's app until one is entered again. */
  async clearInquiryKey(): Promise<boolean> {
    const id = this.editingConnectionId;
    if (!id || this.savingInquiryKey) return false;
    return this.inquiryKeyAction(
      id,
      { action: "clear-inquiry-key" },
      "Removed. HQ cannot put client questions into this app until a key is entered again.",
      "Could not remove the inquiry publishing key",
    );
  }

  private async inquiryKeyAction(id: string, body: Record<string, unknown>, done: string, failed: string): Promise<boolean> {
    this.savingInquiryKey = true;
    this.inquiryKeyError = null;
    this.inquiryKeyNotice = null;
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    try {
      // The RAW client: nothing here is cached, and a refusal rejects.
      const result = (await this.connectionsService?.patch(id, body as never)) as unknown as InstanceConnection | undefined;
      if (left() || this.editingConnectionId !== id) return false;
      if (!result?.id) throw new Error("HQ did not confirm it");
      runInAction(() => {
        this.inquiryKeyDraft = "";
        this.inquiryKeyNotice = done;
      });
      await this.loadConnections();
      return true;
    } catch (err) {
      if (left()) return false;
      runInAction(() => {
        if (!isSessionEnded(err)) this.inquiryKeyError = messageOf(err, failed);
      });
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.savingInquiryKey = false;
        });
      }
    }
  }

  /** Ask HQ to try the key against the instance now. */
  async checkConnection(id = this.editingConnectionId ?? this.selectedConnectionId): Promise<boolean> {
    if (!id) return false;
    this.checkingConnection = true;
    this.keyActionError = null;
    const epoch = this.clientEpoch;
    const left = () => epoch !== this.clientEpoch;
    try {
      const result = (await this.connectionsService?.create({ action: "validate", id } as never)) as unknown as InstanceConnection;
      if (left()) return false;
      await this.loadConnections();
      return !!result?.validation?.ok;
    } catch (err) {
      if (left()) return false;
      runInAction(() => {
        if (!isSessionEnded(err)) this.keyActionError = messageOf(err, "Could not check the connection");
      });
      return false;
    } finally {
      if (!left()) {
        runInAction(() => {
          this.checkingConnection = false;
        });
      }
    }
  }
}

export default InstanceUsersStore;
